// EquipmentGear — R6 Logistics dashboard: expiration alerts, SCBA status,
// unissued inventory, gear collection, fit test compliance, disposal trends.

import { useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import {
  useGearInventory,
  useUniforms,
  useScbaCylinders,
  useScbaG1Cylinders,
  useFitTestsG1,
  useFitTestsAdvantage,
  useDisposals,
  useGearCollection,
} from "../hooks/useOps";
import type {
  GearInventoryRow,
  UniformRow,
  ScbaCylinderRow,
  ScbaG1CylinderRow,
  FitTestG1Row,
  FitTestAdvantageRow,
  DisposalRow,
  GearCollectionRow,
} from "../hooks/useOps";
import { useAggregates } from "../hooks/useDashboard";
import KPICard from "../components/dashboard/KPICard";
import ChartCard from "../components/dashboard/ChartCard";
import AlertList from "../components/dashboard/AlertList";
import StatusBadge from "../components/dashboard/StatusBadge";
import Banner from "../components/shared/Banner";
import { TruncationNoticeGroup } from "../components/shared/TruncationNotice";
import type { AlertItem } from "../components/dashboard/AlertList";
import { useTheme } from "../hooks/useTheme";
import { getChartTheme } from "../lib/chart-theme";
import DateRangeToggle, {
  useSinceDate,
  isAfterSince,
} from "../components/dashboard/DateRangeToggle";
import type { DateRange } from "../components/dashboard/DateRangeToggle";

// ── Brand constants ───────────────────────────────────────────────────────────

const DCVFD_ACCENT = "#2a7a6c";
const COLOR_RED = "#dc2626";
const COLOR_AMBER = "#d97706";
const COLOR_BLUE = "#3b82f6";
const COLOR_MUTED = "#6b7280";

// ── Date helpers ──────────────────────────────────────────────────────────────

/** Parse a date string safely; returns null for invalid/empty values. */
function parseDate(v: string | null | undefined): Date | null {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Days from `now` to a future date (negative = already past). Evaluated at call
 * time. `now` is injectable so a compliance boundary can be tested at a fixed
 * instant instead of drifting with the wall clock.
 */
function daysUntil(d: Date, now: Date = new Date()): number {
  return Math.round((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

/** Format a date string as MM/YYYY, falling back to raw string or '—'. */
function fmtMonth(v: string | null | undefined): string {
  const d = parseDate(v);
  if (!d) return v ?? "—";
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

// ── Field access against the LIVE schema ─────────────────────────────────────
//
// dash_gear_inventory does NOT have retirement_date, issued_date, gear_type,
// member, station or status columns. It has: Expired ("YES"/"NO"), Asset_Type,
// Member_Issued_to, Location, Date_of_Manufacture, Disposal_Date, Condition.
// Every accessor below reads the columns that exist, falls back to the ones the
// UI was originally written against, and returns an explicit UNKNOWN rather
// than a reassuring default when neither is present.

type GearLikeRow = Record<string, unknown>;

const MEMBER_KEYS = ["member", "member_issued_to", "issued_to"];
const TYPE_KEYS = ["gear_type", "asset_type"];
const STATION_KEYS = ["station", "location"];

/** First present, non-empty value among candidate keys; null if none. */
function readStr(row: GearLikeRow, keys: string[]): string | null {
  for (const k of keys) {
    const v = row[k];
    if (typeof v === "string" && v.trim() !== "") return v.trim();
    if (typeof v === "number" && Number.isFinite(v)) return String(v);
  }
  return null;
}

/** True when the record carries at least one of the candidate keys at all. */
function hasAnyKey(row: GearLikeRow, keys: string[]): boolean {
  return keys.some((k) => k in row);
}

/** Gear type label — real column is Asset_Type. */
export function gearTypeLabel(row: GearLikeRow): string {
  return readStr(row, TYPE_KEYS) ?? "Unknown gear";
}

/**
 * Member label. Distinguishes "the field exists and is blank" (genuinely
 * Unassigned) from "the field is not in the source at all" (unknown) — the
 * second must never be reported as the first.
 */
export function gearMemberLabel(row: GearLikeRow): string {
  const v = readStr(row, MEMBER_KEYS);
  if (v) return v;
  return hasAnyKey(row, MEMBER_KEYS) ? "Unassigned" : "Member unknown";
}

/** Station label — real column is Location. */
export function gearStationLabel(row: GearLikeRow): string {
  return readStr(row, STATION_KEYS) ?? "Unknown";
}

/**
 * Is this item unissued? null = cannot tell (no member field in the source).
 * An unanswerable question is never counted as a "yes".
 */
export function gearIsUnissued(row: GearLikeRow): boolean | null {
  if (!hasAnyKey(row, MEMBER_KEYS)) return null;
  return readStr(row, MEMBER_KEYS) === null;
}

// ── Gear expiry status (the `Expired` column that actually exists) ────────────

export type ExpiredStatus = "expired" | "current" | "unknown";

const EXPIRED_TRUE = new Set(["yes", "y", "true", "t", "expired", "1"]);
const EXPIRED_FALSE = new Set([
  "no",
  "n",
  "false",
  "f",
  "current",
  "not expired",
  "0",
]);

/**
 * Resolve a raw `Expired` cell to the canonical tri-state.
 *
 * The live column stores the STRINGS "YES"/"NO" — a naive `Expired === 1` or
 * truthiness test matches nothing, which is how 1,278 expired PPE items were
 * rendered to a fire department as a green all-clear. Anything not positively
 * recognised is "unknown", NEVER "current".
 *
 * Mirrors normalizeExpiredFlag() in src/worker/ops-api.ts; duplicated rather
 * than imported because the frontend bundle must not pull in worker code.
 */
export function normalizeExpiredFlag(value: unknown): ExpiredStatus {
  if (typeof value === "boolean") return value ? "expired" : "current";
  if (typeof value === "number") {
    if (value === 1) return "expired";
    if (value === 0) return "current";
    return "unknown";
  }
  if (typeof value !== "string") return "unknown";
  const v = value
    .trim()
    .replace(/^"+|"+$/g, "")
    .trim()
    .toLowerCase();
  if (v === "") return "unknown";
  if (EXPIRED_TRUE.has(v)) return "expired";
  if (EXPIRED_FALSE.has(v)) return "current";
  return "unknown";
}

/** Row expiry status: the worker's `expired_status` wins, else the raw column. */
export function readExpiredStatus(row: GearLikeRow): ExpiredStatus {
  const served = row["expired_status"];
  if (served === "expired" || served === "current" || served === "unknown") {
    return served;
  }
  return normalizeExpiredFlag(row["expired"]);
}

export interface GearExpirySummary {
  expired: number;
  current: number;
  unknown: number;
  total: number;
}

export interface GearExpiryState extends GearExpirySummary {
  /** false when nothing could be resolved — render "—", never 0. */
  resolved: boolean;
  /** true ONLY when every record is positively recorded as not expired. */
  allClear: boolean;
  source: "server" | "rows" | "none";
}

/**
 * Pull the worker's whole-table `expired_summary` off the gear payload.
 * Returns null (→ fall back to counting rows) unless every field is a number,
 * so a malformed/absent summary can never masquerade as zero expired items.
 */
export function readGearExpirySummary(
  payload: unknown,
): GearExpirySummary | null {
  if (!payload || typeof payload !== "object") return null;
  const raw = (payload as { expired_summary?: unknown }).expired_summary;
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const num = (k: string): number | null =>
    typeof o[k] === "number" && Number.isFinite(o[k] as number)
      ? (o[k] as number)
      : null;
  const expired = num("expired");
  const current = num("current");
  const unknown = num("unknown");
  if (expired === null || current === null || unknown === null) return null;
  return { expired, current, unknown, total: expired + current + unknown };
}

/**
 * Authoritative expiry state for the page. The server summary (whole filtered
 * table) is preferred over the page of rows so pagination cannot under-report.
 */
export function computeGearExpiryState(
  rows: GearLikeRow[],
  summary?: GearExpirySummary | null,
): GearExpiryState {
  let expired = 0;
  let current = 0;
  let unknown = 0;
  let source: GearExpiryState["source"] = "none";

  if (summary && summary.total > 0) {
    ({ expired, current, unknown } = summary);
    source = "server";
  } else if (rows.length > 0) {
    for (const row of rows) {
      const s = readExpiredStatus(row);
      if (s === "expired") expired++;
      else if (s === "current") current++;
      else unknown++;
    }
    source = "rows";
  }

  const total = expired + current + unknown;
  const resolved = total > 0;
  return {
    expired,
    current,
    unknown,
    total,
    resolved,
    allClear: resolved && expired === 0 && unknown === 0,
    source,
  };
}

export interface GearExpiryTile {
  value: string;
  subtitle: string;
  accent: "green" | "red" | "amber";
  trendLabel: string | null;
}

/**
 * KPI presentation for expired gear. Green is reachable ONLY from a fully
 * resolved, zero-expired, zero-unknown inventory. Unresolved renders "—", not
 * "0" — a zero we did not measure is the failure this page exists to prevent.
 */
export function gearExpiryTile(state: GearExpiryState): GearExpiryTile {
  if (!state.resolved) {
    return {
      value: "—",
      subtitle: "Gear inventory not loaded — expiry status unknown",
      accent: "amber",
      trendLabel: null,
    };
  }
  if (state.expired > 0) {
    const unknownNote = state.unknown > 0 ? ` · ${state.unknown} unknown` : "";
    return {
      value: String(state.expired),
      subtitle: `of ${state.total} records flagged Expired${unknownNote}`,
      accent: "red",
      trendLabel: `${state.expired} expired`,
    };
  }
  if (state.unknown > 0) {
    return {
      value: `0 + ${state.unknown}?`,
      subtitle: `0 confirmed expired · ${state.unknown} of ${state.total} records have an unreadable expiry flag`,
      accent: "amber",
      trendLabel: `${state.unknown} unknown`,
    };
  }
  return {
    value: "0",
    subtitle: "All gear current",
    accent: "green",
    trendLabel: "All current",
  };
}

/**
 * C-4 cross-check: the pre-computed dash_aggregates value is refreshed by an
 * off-repo runner and can go silently stale. We always show the live read; if
 * the two disagree we say so rather than quietly picking one.
 */
export function coerceCount(v: unknown): number | null {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v))) {
    return Number(v);
  }
  return null;
}

export function aggregateReconNote(
  state: GearExpiryState,
  aggExpired: number | null,
): string | null {
  if (!state.resolved || aggExpired === null) return null;
  if (aggExpired === state.expired) return null;
  return `Pre-computed aggregate reports ${aggExpired} expired items; the live inventory read reports ${state.expired}. Showing the live read — the aggregate may be stale.`;
}

export interface ActiveGearCount {
  count: number | null;
  /** "status" = a real status column; "derived" = not disposed and not expired. */
  basis: "status" | "derived" | "none";
}

/**
 * Items in service. dash_gear_inventory has NO status column, so the old
 * `status !== "retired"` test counted every record — including the 1,278
 * flagged Expired — as active. With no status field we derive from the columns
 * that exist: in service = not disposed AND positively not expired. Records
 * with an unreadable expiry flag are NOT counted as in service.
 */
export function computeActiveGear(rows: GearLikeRow[]): ActiveGearCount {
  if (rows.length === 0) return { count: null, basis: "none" };

  if (rows.some((r) => "status" in r)) {
    return {
      count: rows.filter(
        (r) => (readStr(r, ["status"]) ?? "").toLowerCase() !== "retired",
      ).length,
      basis: "status",
    };
  }

  const count = rows.filter(
    (r) =>
      readExpiredStatus(r) === "current" &&
      readStr(r, ["disposal_date"]) === null,
  ).length;
  return { count, basis: "derived" };
}

// ── Section 1: Gear Expiration Alerts ─────────────────────────────────────────

/** Gear lifecycle in years by gear_type (NFPA 1851 defaults). */
const GEAR_LIFECYCLE_YEARS: Record<string, number> = {
  coat: 10,
  pants: 10,
  helmet: 10,
  hood: 5,
  gloves: 5,
  boots: 10,
  default: 10,
};

function gearLifecycleYears(row: GearInventoryRow): number {
  const key = (readStr(row, TYPE_KEYS) ?? "").toLowerCase();
  for (const [k, v] of Object.entries(GEAR_LIFECYCLE_YEARS)) {
    if (key.includes(k)) return v;
  }
  return GEAR_LIFECYCLE_YEARS.default;
}

export interface GearAlert {
  row: GearInventoryRow;
  daysRemaining: number | null;
  reason: "expired" | "critical" | "unknown" | "warning" | "info";
  /** true when the date is derived from issued_date + NFPA lifecycle, not a recorded retirement_date. */
  estimated: boolean;
}

/** Display order: recorded expiry first, then imminent, then unreadable. */
const ALERT_RANK: Record<GearAlert["reason"], number> = {
  expired: 0,
  critical: 1,
  unknown: 2,
  warning: 3,
  info: 4,
};

export function computeGearAlerts(rows: GearInventoryRow[]): GearAlert[] {
  const alerts: GearAlert[] = [];

  for (const row of rows) {
    // The recorded `Expired` flag is the primary fact on the live schema and
    // the only expiry signal most rows carry. It is checked FIRST — the
    // date branches below reference columns (retirement_date, issued_date)
    // that do not exist on dash_gear_inventory, so relying on them alone
    // produced a structurally empty alert list for all 4,100 records.
    const flag = readExpiredStatus(row);
    if (flag === "expired") {
      alerts.push({
        row,
        daysRemaining: null,
        reason: "expired",
        estimated: false,
      });
      continue;
    }
    if (flag === "unknown") {
      // Unreadable/absent expiry flag — surfaced, never assumed current.
      alerts.push({
        row,
        daysRemaining: null,
        reason: "unknown",
        estimated: false,
      });
      continue;
    }

    // Flag says not expired. Lifecycle dates can still raise an upcoming (or
    // contradicting) expiry when the source carries them.
    // Explicit retirement date takes precedence — this is a recorded fact.
    const retDate = parseDate(row.retirement_date);
    if (retDate) {
      const days = daysUntil(retDate);
      if (days < 0) {
        alerts.push({
          row,
          daysRemaining: days,
          reason: "expired",
          estimated: false,
        });
      } else if (days <= 90) {
        alerts.push({
          row,
          daysRemaining: days,
          reason: "critical",
          estimated: false,
        });
      } else if (days <= 180) {
        alerts.push({
          row,
          daysRemaining: days,
          reason: "warning",
          estimated: false,
        });
      } else if (days <= 365) {
        alerts.push({
          row,
          daysRemaining: days,
          reason: "info",
          estimated: false,
        });
      }
      continue;
    }

    // Compute from issued_date + lifecycle — this is an ESTIMATE, not a record.
    const issuedDate = parseDate(row.issued_date);
    if (issuedDate) {
      const lifecycleYrs = gearLifecycleYears(row);
      const retireCalc = new Date(issuedDate);
      retireCalc.setFullYear(retireCalc.getFullYear() + lifecycleYrs);
      const days = daysUntil(retireCalc);
      if (days < 0) {
        alerts.push({
          row,
          daysRemaining: days,
          reason: "expired",
          estimated: true,
        });
      } else if (days <= 90) {
        alerts.push({
          row,
          daysRemaining: days,
          reason: "critical",
          estimated: true,
        });
      } else if (days <= 180) {
        alerts.push({
          row,
          daysRemaining: days,
          reason: "warning",
          estimated: true,
        });
      } else if (days <= 365) {
        alerts.push({
          row,
          daysRemaining: days,
          reason: "info",
          estimated: true,
        });
      }
    }
  }

  // Sort: recorded-expired first, then soonest upcoming, then unreadable.
  return alerts.sort((a, b) => {
    const rankDiff = ALERT_RANK[a.reason] - ALERT_RANK[b.reason];
    if (rankDiff !== 0) return rankDiff;
    // Nulls (flag-derived, no date) sort last within their rank.
    const ra = a.daysRemaining ?? Number.POSITIVE_INFINITY;
    const rb = b.daysRemaining ?? Number.POSITIVE_INFINITY;
    return ra - rb;
  });
}

export function gearAlertToAlertItem(a: GearAlert, index: number): AlertItem {
  const member = gearMemberLabel(a.row);
  const type = gearTypeLabel(a.row);
  const serial = a.row.serial_number ? ` #${a.row.serial_number}` : "";
  const station = gearStationLabel(a.row);
  // Mark lifecycle-derived alerts as estimates so a board reader can tell an
  // assumed NFPA service-life expiry from a recorded retirement date.
  const est = a.estimated ? " (est.)" : "";

  let meta: string;
  let severity: AlertItem["severity"];

  if (a.reason === "unknown") {
    // Not "current" — we could not read the flag at all.
    severity = "warning";
    meta = "Expiry unknown";
  } else if (a.reason === "expired" && a.daysRemaining === null) {
    // Recorded Expired=YES with no date to age it against.
    severity = "critical";
    meta = "Expired (recorded)";
  } else if (
    a.reason === "expired" ||
    (a.daysRemaining !== null && a.daysRemaining < 0)
  ) {
    severity = "critical";
    meta = `Expired ${Math.abs(a.daysRemaining ?? 0)}d ago${est}`;
  } else if (a.daysRemaining !== null && a.daysRemaining <= 90) {
    severity = "critical";
    meta = `${a.daysRemaining}d left${est}`;
  } else if (a.daysRemaining !== null && a.daysRemaining <= 180) {
    severity = "warning";
    meta = `${a.daysRemaining}d left${est}`;
  } else {
    severity = "info";
    meta =
      a.daysRemaining !== null ? `${a.daysRemaining}d left${est}` : "Review";
  }

  // Stable key from record identity — never Math.random() (which would remount
  // every poll cycle, losing scroll/focus and breaking reconciliation).
  const stableId =
    a.row.airtable_id ??
    a.row.id ??
    `${a.row.serial_number ?? ""}|${type}|${member}|${index}`;

  // Only claim an issue date when one is actually recorded; otherwise fall back
  // to date of manufacture (labelled as such) or say unknown. Never print a
  // dash where the UI implies a known date.
  const issued = readStr(a.row, ["issued_date"]);
  const mfr = readStr(a.row, ["date_of_manufacture"]);
  const datePart = issued
    ? `Issued: ${fmtMonth(issued)}`
    : mfr
      ? `Mfr: ${fmtMonth(mfr)}`
      : "Issued: unknown";

  return {
    id: String(stableId),
    title: `${type}${serial} — ${member}${a.estimated ? " · est. service life" : ""}`,
    subtitle: `Station: ${station} · ${datePart}`,
    severity,
    meta,
  };
}

// ── Section 2: SCBA Cylinder Status ──────────────────────────────────────────
//
// LIVE SCHEMA (verified against remote D1, 2026-08-27). NEITHER table has a
// `hydro_expiry`, a `retire_date`, or a `station` column — the originals this
// section was written against do not exist, so every read returned undefined
// and all 570 cylinders fell through to `current`: a permanent 100% all-clear
// over a fleet that is almost entirely out of test.
//
//   dash_scba_cylinders    (352 rows) → cylinder_number, cylinder_type,
//                                       manufacture_date, end_of_life,
//                                       test_dates, nex_test_due
//   dash_scba_g1_cylinders (218 rows) → serial_number, category,
//                                       manufacture_date, hydro_due_date,
//                                       status, hydro_test_date
//
// Keys are the worker's toSnakeCase() form of the real column names.
// `nex_test_due` is NOT a typo here — the live column really is `Nex_Test_Due`
// with one 't'. Renaming it upstream must break these tests, not this page.
// dash_scba_g1_cylinders has NO end-of-life column at all; a G1 row can
// therefore never be classified end-of-life, only in/out of hydro test.

// The two lists below drive CLASSIFICATION, so they carry no speculative
// fallbacks. `hydro_expiry` and `retire_date` are deliberately absent: they
// exist on neither table, and a key that can never match is exactly the dead
// path that produced the all-clear. A date field we cannot find must yield
// UNKNOWN, not quietly try one more name.

/** Next test / hydro due date. Real: Nex_Test_Due (352) | Hydro_Due_Date (218). */
export const SCBA_TEST_DUE_KEYS = ["nex_test_due", "hydro_due_date"];
/** Retirement / end-of-life date. Real: End_of_Life. G1 has none. */
export const SCBA_RETIRE_KEYS = ["end_of_life"];
/** Identifier. Real: Cylinder_Number (352) | Serial_Number (218). */
export const SCBA_SERIAL_KEYS = [
  "cylinder_number",
  "serial_number",
  "cylinder_id",
];
/** Kind of cylinder. Real: Cylinder_Type (352) | Category (218). */
export const SCBA_TYPE_KEYS = ["cylinder_type", "category"];

export type ScbaBucket = "current" | "needsTesting" | "endOfLife" | "unknown";

export interface ScbaClassification {
  bucket: ScbaBucket;
  /** Raw test-due value as recorded, or null when the row carries none. */
  testDueRaw: string | null;
  /** Raw retire value as recorded, or null when the row carries none. */
  retireRaw: string | null;
  /** Why the row is unknown; null when it was positively classified. */
  unknownReason: "no test date" | "unreadable date" | null;
}

/**
 * Classify one cylinder.
 *
 * The single rule that matters: **absence of a date is not evidence of
 * compliance.** A row we cannot date is UNKNOWN and is never folded into
 * `current`. A date that is recorded but unparseable is likewise unknown — it
 * is missing data wearing a value.
 *
 * Precedence: a definite failure outranks uncertainty, and uncertainty
 * outranks a pass.
 *   1. retire date readable and past      → endOfLife   (definite)
 *   2. test due readable and past         → needsTesting (definite)
 *   3. any recorded date unreadable       → unknown
 *   4. test due readable and in future    → current
 *   5. no test due recorded at all        → unknown
 */
export function classifyScbaRow(row: GearLikeRow): ScbaClassification {
  const testDueRaw = readStr(row, SCBA_TEST_DUE_KEYS);
  const retireRaw = readStr(row, SCBA_RETIRE_KEYS);
  const testDue = parseDate(testDueRaw);
  const retire = parseDate(retireRaw);

  const base = { testDueRaw, retireRaw };

  // 1 — past its retirement date. Definite, regardless of test data.
  if (retire && daysUntil(retire) < 0) {
    return { ...base, bucket: "endOfLife", unknownReason: null };
  }
  // 2 — hydro/next test date has passed. Definite.
  if (testDue && daysUntil(testDue) < 0) {
    return { ...base, bucket: "needsTesting", unknownReason: null };
  }
  // 3 — something was recorded but could not be read. Never a pass.
  if ((retireRaw !== null && !retire) || (testDueRaw !== null && !testDue)) {
    return { ...base, bucket: "unknown", unknownReason: "unreadable date" };
  }
  // 4 — positively in test: a real due date, still in the future.
  if (testDue) {
    return { ...base, bucket: "current", unknownReason: null };
  }
  // 5 — no test date on file. Unaccounted for, NOT current.
  return { ...base, bucket: "unknown", unknownReason: "no test date" };
}

interface ScbaStats {
  current: number;
  needsTesting: number;
  endOfLife: number;
  /**
   * Cylinders with no usable test date. Deliberately NOT added to `current` —
   * this counter existing is the fix.
   */
  unknown: number;
  total: number;
  /** False when nothing loaded, so the UI shows "—" instead of a green zero. */
  resolved: boolean;
  attentionRows: Array<{
    id: string;
    serial: string;
    kind: string;
    status: string;
    testDue: string;
    retire: string;
    issue: string;
  }>;
}

const SCBA_ISSUE_LABEL: Record<Exclude<ScbaBucket, "current">, string> = {
  endOfLife: "End of Life",
  needsTesting: "Hydro Overdue",
  unknown: "No test date",
};

export function computeScbaStats(
  cylinders: ScbaCylinderRow[],
  g1Cylinders: ScbaG1CylinderRow[],
): ScbaStats {
  let current = 0;
  let needsTesting = 0;
  let endOfLife = 0;
  let unknown = 0;
  const attentionRows: ScbaStats["attentionRows"] = [];

  const allRows: Array<ScbaCylinderRow | ScbaG1CylinderRow> = [
    ...cylinders,
    ...g1Cylinders,
  ];

  allRows.forEach((row, index) => {
    const c = classifyScbaRow(row);

    if (c.bucket === "current") {
      current++;
      return;
    }
    if (c.bucket === "endOfLife") endOfLife++;
    else if (c.bucket === "needsTesting") needsTesting++;
    else unknown++;

    const serial = readStr(row, SCBA_SERIAL_KEYS);
    attentionRows.push({
      id: String(
        readStr(row, ["airtable_id", "id"]) ??
          `${serial ?? ""}|${c.testDueRaw ?? ""}|${index}`,
      ),
      serial: serial ?? "Unidentified",
      kind: readStr(row, SCBA_TYPE_KEYS) ?? "Unknown",
      status: readStr(row, ["status"]) ?? "—",
      testDue: c.testDueRaw ? fmtMonth(c.testDueRaw) : "None on file",
      retire: c.retireRaw ? fmtMonth(c.retireRaw) : "None on file",
      issue:
        c.bucket === "unknown" && c.unknownReason === "unreadable date"
          ? "Unreadable date"
          : SCBA_ISSUE_LABEL[c.bucket],
    });
  });

  return {
    current,
    needsTesting,
    endOfLife,
    unknown,
    total: allRows.length,
    resolved: allRows.length > 0,
    attentionRows,
  };
}

export interface ScbaTile {
  value: string;
  subtitle: string;
  accent: "green" | "amber" | "red" | "neutral";
}

/**
 * The "Current" tile.
 *
 * It must be impossible to read a reassuring number here while cylinders are
 * unaccounted for. When any row is UNKNOWN the value itself carries the count
 * ("23 + 12?") — a footnote in a drawer is not disclosure. Green is reserved
 * for the one honest case: every cylinder resolved, and every one in test.
 */
export function scbaCurrentTileFor(stats: ScbaStats): ScbaTile {
  if (!stats.resolved) {
    return {
      value: "—",
      subtitle: "SCBA cylinder data not loaded — status unknown",
      accent: "amber",
    };
  }
  if (stats.unknown > 0) {
    return {
      value: `${stats.current} + ${stats.unknown}?`,
      subtitle:
        `${stats.current} of ${stats.total} confirmed in test · ` +
        `${stats.unknown} with no usable test date (not counted as current)`,
      accent: "amber",
    };
  }
  if (stats.current === stats.total) {
    return {
      value: String(stats.current),
      subtitle: `all ${stats.total} cylinders in test/service`,
      accent: "green",
    };
  }
  return {
    value: String(stats.current),
    subtitle: `of ${stats.total} cylinders confirmed in test/service`,
    accent: "neutral",
  };
}

// ── Section 3: Unissued Inventory ─────────────────────────────────────────────

interface UnissuedGroup {
  label: string;
  gear: number;
  uniforms: number;
  total: number;
}

/**
 * Unissued counts. Neither dash_gear_inventory nor dash_uniforms has a `member`
 * column — the real ones are Member_Issued_to / Issued_to — so the old
 * `!r.member` predicate counted EVERY record as unissued (4,100 gear items).
 * Records where no member field exists at all are reported as unknown, not
 * folded into the unissued total.
 */
export function computeUnissuedCounts(
  gear: GearInventoryRow[],
  uniforms: UniformRow[],
): { unissued: number; unknown: number; resolved: boolean } {
  let unissued = 0;
  let unknown = 0;
  for (const r of [...gear, ...uniforms]) {
    const state = gearIsUnissued(r);
    if (state === null) unknown++;
    else if (state) unissued++;
  }
  return {
    unissued,
    unknown,
    resolved: gear.length + uniforms.length > 0,
  };
}

function computeUnissued(
  gear: GearInventoryRow[],
  uniforms: UniformRow[],
): UnissuedGroup[] {
  const gearUnissued = gear.filter((r) => gearIsUnissued(r) === true);
  const unifUnissued = uniforms.filter((r) => gearIsUnissued(r) === true);

  // Group gear by type (Asset_Type on the live schema)
  const gearByType = new Map<string, number>();
  for (const r of gearUnissued) {
    const k = gearTypeLabel(r);
    gearByType.set(k, (gearByType.get(k) ?? 0) + 1);
  }

  // Group uniforms by uniform_type + size
  const unifByGroup = new Map<string, number>();
  for (const r of unifUnissued) {
    const type = readStr(r, ["uniform_type", "asset_type"]) ?? "Uniform";
    const k = `${type}${r.size ? ` (${r.size})` : ""}`;
    unifByGroup.set(k, (unifByGroup.get(k) ?? 0) + (r.quantity ?? 1));
  }

  const combined = new Map<string, { gear: number; uniforms: number }>();

  for (const [k, v] of gearByType.entries()) {
    combined.set(k, { gear: v, uniforms: 0 });
  }
  for (const [k, v] of unifByGroup.entries()) {
    const cur = combined.get(k) ?? { gear: 0, uniforms: 0 };
    combined.set(k, { ...cur, uniforms: cur.uniforms + v });
  }

  return Array.from(combined.entries())
    .map(([label, counts]) => ({
      label,
      gear: counts.gear,
      uniforms: counts.uniforms,
      total: counts.gear + counts.uniforms,
    }))
    .sort((a, b) => b.total - a.total);
}

// ── Section 4: Gear Assigned to Inactive Members ──────────────────────────────

interface GearCollectionAlert {
  member: string;
  collectionDate: string;
  /** What the member still owes — dash_gear_collection."Items_to_Return". */
  items: string;
  status: "recovery_in_progress" | "pending";
}

/**
 * Turn-in recovery alerts.
 *
 * The columns are `name`, `created_on` and `items_to_return`. This read
 * `member` / `collection_date` / `gear_type` / `station` — none of which are
 * on this table — so all 51 live turn-ins rendered "Unknown Member · Unknown ·
 * —" with no date. There is NO station or gear-type column here; the honest
 * subtitle is what is owed, not a fabricated station.
 */
export function computeCollectionAlerts(
  collection: GearCollectionRow[],
): GearCollectionAlert[] {
  return collection.map((r) => ({
    member: r.name ?? "Unknown Member",
    collectionDate: fmtMonth(r.created_on),
    items: r.items_to_return ?? "Unspecified items",
    status: "recovery_in_progress",
  }));
}

function collectionToAlertItem(a: GearCollectionAlert, idx: number): AlertItem {
  return {
    id: `collection-${idx}`,
    title: a.member,
    subtitle: a.items,
    severity: "warning",
    meta: `Recovery · ${a.collectionDate}`,
  };
}

// ── Section 5: Fit Test Compliance ────────────────────────────────────────────

interface FitTestStatus {
  member: string;
  testType: string;
  lastTest: string | null;
  dueDate: string | null;
  status: "current" | "expiring" | "expired" | "no_record" | "unknown";
}

/**
 * Respirator fit-test currency, per record.
 *
 * THE COLUMN NAMES ARE THE WHOLE BUG. This read `member` / `test_date` /
 * `expiry_date`. dash_fit_test_g1 spells them `Members` (PLURAL),
 * `Last_Test_Date` and `Test_Date_Due`; dash_fit_test_advantage spells the
 * first `Member` (singular) and the other two the same. Every read therefore
 * returned undefined, `expiryD` was always null, and every record fell to
 * `no_record` — which the non-compliant table and the "N expired" badge both
 * FILTER OUT. Live on 2026-08-27 that rendered "0 expired" over 163 G1 and 163
 * Advantage records whose due dates had ALL already passed (latest 2026-04-04).
 *
 * `now` is injectable so the current/expiring/expired boundaries are testable
 * rather than dependent on the wall clock.
 */
export function computeFitTestStatuses(
  g1: FitTestG1Row[],
  advantage: FitTestAdvantageRow[],
  now: Date = new Date(),
): FitTestStatus[] {
  const all = [
    // `members` on G1, `member` on Advantage — normalized to one field HERE so
    // no downstream reader has to know which feed a row came from.
    ...g1.map((r) => ({
      memberName: r.members ?? null,
      lastTestDate: r.last_test_date ?? null,
      dueDate: r.test_date_due ?? null,
      testType: "G1",
    })),
    ...advantage.map((r) => ({
      memberName: r.member ?? null,
      lastTestDate: r.last_test_date ?? null,
      dueDate: r.test_date_due ?? null,
      testType: "Advantage",
    })),
  ];

  return all.map((r) => {
    const member = r.memberName ?? "Unknown";
    const testType = r.testType;
    const lastTest = r.lastTestDate ?? null;
    const dueDate = r.dueDate ?? null;
    const expiryD = parseDate(dueDate);

    let status: FitTestStatus["status"];
    if (!expiryD) {
      // Has a test date but no expiry — can't confirm currency
      status = lastTest ? "unknown" : "no_record";
    } else {
      const days = daysUntil(expiryD, now);
      if (days < 0) status = "expired";
      else if (days <= 90) status = "expiring";
      else status = "current";
    }

    return { member, testType, lastTest, dueDate, status };
  });
}

interface FitTestDonutDatum {
  name: string;
  value: number;
  color: string;
}

export function computeFitTestDonut(
  statuses: FitTestStatus[],
): FitTestDonutDatum[] {
  const counts = {
    current: 0,
    expiring: 0,
    expired: 0,
    no_record: 0,
    unknown: 0,
  };
  for (const s of statuses) counts[s.status]++;

  return [
    { name: "Current", value: counts.current, color: DCVFD_ACCENT },
    { name: "Expiring", value: counts.expiring, color: COLOR_AMBER },
    { name: "Expired", value: counts.expired, color: COLOR_RED },
    { name: "Unknown", value: counts.unknown, color: COLOR_BLUE },
    { name: "No Record", value: counts.no_record, color: COLOR_MUTED },
  ].filter((d) => d.value > 0);
}

// ── Section 6: Disposal Trends ────────────────────────────────────────────────

interface DisposalMonthBucket {
  month: string; // "Jan 2025"
  count: number;
  value: number;
}

/**
 * Monthly disposal counts and value.
 *
 * The date column is `date_of_disposal`. This read `disposal_date`, which does
 * not exist, so `parseDate(undefined)` was null for all 462 live rows and every
 * one was skipped — the trend chart was structurally incapable of showing
 * anything. 452 of the 462 carry a real date spanning 2018-01 to 2025-03.
 */
export function computeDisposalTrends(
  rows: DisposalRow[],
): DisposalMonthBucket[] {
  const buckets = new Map<string, { count: number; value: number }>();

  for (const row of rows) {
    const d = parseDate(row.date_of_disposal);
    if (!d) continue;

    const label = d.toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    });
    const cur = buckets.get(label) ?? { count: 0, value: 0 };
    const val =
      typeof (row as unknown as Record<string, unknown>)["approx_value"] ===
      "number"
        ? ((row as unknown as Record<string, unknown>)[
            "approx_value"
          ] as number)
        : 0;
    buckets.set(label, { count: cur.count + 1, value: cur.value + val });
  }

  // Sort chronologically
  return Array.from(buckets.entries())
    .map(([month, v]) => ({ month, ...v }))
    .sort((a, b) => new Date(a.month).getTime() - new Date(b.month).getTime())
    .slice(-18); // last 18 months
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-sm font-semibold text-theme-muted uppercase tracking-wider leading-none mb-3">
      {children}
    </h2>
  );
}

function TableSkeleton({ cols, rows = 5 }: { cols: number; rows?: number }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-theme-subtle">
            {Array.from({ length: cols }).map((_, i) => (
              <th key={i} className="px-4 py-3">
                <div className="skeleton h-3 w-20 rounded-md" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, i) => (
            <tr key={i} className="border-b border-theme-subtle">
              {Array.from({ length: cols }).map((_, j) => (
                <td key={j} className="px-4 py-3">
                  <div
                    className="skeleton h-4 rounded-md"
                    style={{ width: `${60 + (j % 3) * 20}%` }}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── SCBA Table ────────────────────────────────────────────────────────────────

function ScbaAttentionTable({
  rows,
  isLoading,
}: {
  rows: ScbaStats["attentionRows"];
  isLoading: boolean;
}) {
  if (isLoading) return <TableSkeleton cols={5} />;

  if (rows.length === 0) {
    return (
      <div className="px-4 py-8 text-center text-theme-muted text-sm">
        No SCBA cylinders are flagged by the data on file. This is not evidence
        of compliance: see the notice at the top of the page.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-theme-subtle">
            {[
              "Serial / ID",
              "Type",
              "Next Test Due",
              "End of Life",
              "Issue",
            ].map((h) => (
              <th
                key={h}
                className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              className="hover:bg-theme-overlay/50 transition-colors"
            >
              <td className="px-4 py-3 text-sm font-medium text-theme-primary">
                {r.serial}
              </td>
              <td className="px-4 py-3 text-sm text-theme-secondary">
                {r.kind}
              </td>
              <td className="px-4 py-3 text-sm text-theme-secondary tabular-nums">
                {r.testDue}
              </td>
              <td className="px-4 py-3 text-sm text-theme-secondary tabular-nums">
                {r.retire}
              </td>
              <td className="px-4 py-3">
                <span
                  className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${
                    r.issue === "End of Life"
                      ? "bg-red-500/15 text-red-700 dark:text-red-400 border border-red-500/30"
                      : r.issue === "Hydro Overdue"
                        ? "bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30"
                        : "bg-slate-500/15 text-slate-700 dark:text-slate-300 border border-slate-500/40"
                  }`}
                >
                  {r.issue}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Unissued Inventory Table ──────────────────────────────────────────────────

/**
 * What an EMPTY unissued table actually means. An empty list is only an
 * all-clear when records were in view and every one of them had a member; if
 * nothing was in view, or some records carry no member field at all, we say so
 * rather than printing a reassuring "all assigned".
 */
export function unissuedEmptyMessage(counts: {
  unissued: number;
  unknown: number;
  resolved: boolean;
}): string {
  if (!counts.resolved) {
    return "No gear or uniform records in view — assignment status unknown.";
  }
  if (counts.unknown > 0) {
    return `${counts.unknown} record${counts.unknown === 1 ? "" : "s"} carry no member field — assignment status unknown.`;
  }
  return "All inventory items are assigned.";
}

function UnissuedTable({
  groups,
  isLoading,
  emptyMessage,
}: {
  groups: UnissuedGroup[];
  isLoading: boolean;
  emptyMessage: string;
}) {
  if (isLoading) return <TableSkeleton cols={4} />;

  if (groups.length === 0) {
    return (
      <div className="px-4 py-8 text-center text-theme-muted text-sm">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-theme-subtle">
            {["Type / Size", "Gear", "Uniforms", "Total"].map((h) => (
              <th
                key={h}
                className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider last:text-right"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <tr
              key={g.label}
              className="hover:bg-theme-overlay/50 transition-colors"
            >
              <td className="px-4 py-3 text-sm font-medium text-theme-primary">
                {g.label}
              </td>
              <td className="px-4 py-3 text-sm text-theme-secondary tabular-nums">
                {g.gear > 0 ? g.gear : "—"}
              </td>
              <td className="px-4 py-3 text-sm text-theme-secondary tabular-nums">
                {g.uniforms > 0 ? g.uniforms : "—"}
              </td>
              <td className="px-4 py-3 text-sm font-bold text-theme-primary tabular-nums text-right">
                {g.total}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Fit Test Non-Compliance Table ─────────────────────────────────────────────

function FitTestNonCompliantTable({
  statuses,
  isLoading,
}: {
  statuses: FitTestStatus[];
  isLoading: boolean;
}) {
  if (isLoading) return <TableSkeleton cols={4} />;

  const nonCompliant = statuses.filter(
    (s) => s.status === "expired" || s.status === "expiring",
  );

  if (nonCompliant.length === 0) {
    return (
      <div className="px-4 py-8 text-center text-theme-muted text-sm">
        No fit tests are flagged by the data on file. This is not evidence of
        compliance: see the notice at the top of the page.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-theme-subtle">
            {["Member", "Type", "Last Test", "Due Date", "Status"].map((h) => (
              <th
                key={h}
                className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {nonCompliant.map((s, idx) => (
            <tr
              key={`${s.member}-${s.testType}-${idx}`}
              className="hover:bg-theme-overlay/50 transition-colors"
            >
              <td className="px-4 py-3 text-sm font-medium text-theme-primary">
                {s.member}
              </td>
              <td className="px-4 py-3 text-sm text-theme-secondary">
                {s.testType}
              </td>
              <td className="px-4 py-3 text-sm text-theme-secondary tabular-nums">
                {fmtMonth(s.lastTest)}
              </td>
              <td className="px-4 py-3 text-sm text-theme-secondary tabular-nums">
                {fmtMonth(s.dueDate)}
              </td>
              <td className="px-4 py-3">
                {s.status === "expired" ? (
                  <StatusBadge label="Expired" variant="red" />
                ) : (
                  <StatusBadge label="Expiring Soon" variant="yellow" />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Disposal Chart ────────────────────────────────────────────────────────────

function DisposalBarChart({
  data,
  isLoading,
}: {
  data: DisposalMonthBucket[];
  isLoading: boolean;
}) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const axisColor = isDark ? "#9ca3af" : "#6b7280";
  const gridColor = isDark ? "#27272a" : "#e5e7eb";

  if (isLoading) {
    return <div className="skeleton rounded-xl" style={{ height: 240 }} />;
  }

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-40 text-sm text-theme-muted">
        No disposal records found.
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart
        data={data}
        margin={{ top: 10, right: 20, left: 0, bottom: 40 }}
        barCategoryGap="20%"
      >
        <CartesianGrid
          vertical={false}
          stroke={gridColor}
          strokeDasharray="3 3"
        />
        <XAxis
          dataKey="month"
          tick={{ fontSize: 10, fill: axisColor }}
          tickLine={false}
          axisLine={false}
          angle={-40}
          textAnchor="end"
          interval={0}
        />
        <YAxis
          yAxisId="count"
          tick={{ fontSize: 11, fill: axisColor }}
          tickLine={false}
          axisLine={false}
          width={36}
          allowDecimals={false}
        />
        <Tooltip
          content={({ active, payload }) => {
            if (!active || !payload?.length) return null;
            const d = payload[0].payload as DisposalMonthBucket;
            return (
              <div className="glass rounded-lg px-3 py-2 text-xs shadow-lg">
                <p className="font-semibold text-theme-primary">{d.month}</p>
                <p className="text-theme-secondary mt-0.5">
                  <span className="font-bold" style={{ color: DCVFD_ACCENT }}>
                    {d.count}
                  </span>{" "}
                  disposal{d.count !== 1 ? "s" : ""}
                </p>
                {d.value > 0 && (
                  <p className="text-theme-muted mt-0.5">
                    ~${d.value.toLocaleString()} estimated value
                  </p>
                )}
              </div>
            );
          }}
          cursor={{
            fill: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
          }}
        />
        <Bar
          yAxisId="count"
          dataKey="count"
          fill={DCVFD_ACCENT}
          fillOpacity={0.85}
          radius={[4, 4, 0, 0]}
          maxBarSize={40}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Fit Test Bar Chart (replaces donut — avoids >4-category pie) ─────────────

interface FitTestBarTooltipProps {
  active?: boolean;
  payload?: Array<{ value: number; payload: FitTestDonutDatum }>;
}

function FitTestBarTooltip({ active, payload }: FitTestBarTooltipProps) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="glass rounded-lg px-3 py-2 text-xs shadow-lg">
      <p className="font-semibold text-theme-primary">{d.name}</p>
      <p className="text-theme-secondary">
        {d.value} member{d.value !== 1 ? "s" : ""}
      </p>
    </div>
  );
}

function FitTestDonut({
  data,
  isLoading,
}: {
  data: FitTestDonutDatum[];
  isLoading: boolean;
}) {
  const { resolvedTheme } = useTheme();
  const chart = getChartTheme(resolvedTheme === "dark");

  if (isLoading) {
    return (
      <div className="flex items-end gap-3 h-48 px-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex-1">
            <div
              className="skeleton w-full rounded-t-sm"
              style={{ height: `${30 + ((i * 17 + 13) % 55)}%` }}
            />
          </div>
        ))}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-40 gap-2 text-center">
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-theme-muted opacity-50"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 16v-4M12 8h.01" />
        </svg>
        <p className="text-sm text-theme-muted">Awaiting data sync</p>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} margin={{ top: 10, right: 8, left: 0, bottom: 8 }}>
        <CartesianGrid
          vertical={false}
          stroke={chart.gridStroke}
          strokeDasharray="3 3"
        />
        <XAxis
          dataKey="name"
          tick={{ fontSize: 10, fill: chart.tickColor }}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          tick={{ fontSize: 11, fill: chart.tickColor }}
          tickLine={false}
          axisLine={false}
          width={32}
          allowDecimals={false}
        />
        <Tooltip
          content={<FitTestBarTooltip />}
          cursor={{ fill: chart.cursorFill }}
        />
        <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={48}>
          {data.map((entry, idx) => (
            <Cell key={`ft-${idx}`} fill={entry.color} fillOpacity={0.85} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function EquipmentGear() {
  const [range, setRange] = useState<DateRange>("all");
  const sinceDate = useSinceDate(range);

  // ── Data fetching ─────────────────────────────────────────────────────────
  const { data: aggData } = useAggregates();
  const { data: gearData, isLoading: gearLoading } = useGearInventory();
  const { data: uniformData, isLoading: uniformLoading } = useUniforms();
  const { data: scbaData, isLoading: scbaLoading } = useScbaCylinders();
  const { data: scbaG1Data, isLoading: scbaG1Loading } = useScbaG1Cylinders();
  const { data: fitG1Data, isLoading: fitG1Loading } = useFitTestsG1();
  const { data: fitAdvData, isLoading: fitAdvLoading } = useFitTestsAdvantage();
  const { data: disposalData, isLoading: disposalLoading } = useDisposals();
  const { data: collectionData, isLoading: collectionLoading } =
    useGearCollection();

  // ── Row completeness ──────────────────────────────────────────────────────
  // This page reads EIGHT ops tables and rolls them into compliance tiles. Two
  // of them sit close to the API's 5,000-row hard cap (gear 4,100 · uniforms
  // 4,179 as of 2026-08-27), and six of them used to ride a silent 500-row
  // default. One notice covers all eight so a partial read cannot present as a
  // readiness figure. (Count corrected from "seven" - the array below always
  // held eight, and a comment that miscounts the sources of a readiness figure
  // is the kind of thing a reviewer trusts instead of checking.)
  const truncationSources = [
    { label: "Gear inventory", response: gearData },
    { label: "Uniforms", response: uniformData },
    { label: "SCBA cylinders", response: scbaData },
    { label: "SCBA G1 cylinders", response: scbaG1Data },
    { label: "G1 fit tests", response: fitG1Data },
    { label: "Advantage fit tests", response: fitAdvData },
    { label: "Disposals", response: disposalData },
    { label: "Gear collection", response: collectionData },
  ];

  const allGear: GearInventoryRow[] = gearData?.data ?? [];
  const allUniforms: UniformRow[] = uniformData?.data ?? [];
  const allScba: ScbaCylinderRow[] = scbaData?.data ?? [];
  const allScbaG1: ScbaG1CylinderRow[] = scbaG1Data?.data ?? [];
  const allFitG1: FitTestG1Row[] = fitG1Data?.data ?? [];
  const allFitAdv: FitTestAdvantageRow[] = fitAdvData?.data ?? [];
  const allDisposals: DisposalRow[] = disposalData?.data ?? [];
  const allCollection: GearCollectionRow[] = collectionData?.data ?? [];

  // ── Apply date range filter ───────────────────────────────────────────────
  const gear = useMemo(
    () =>
      !sinceDate
        ? allGear
        : allGear.filter((r) =>
            isAfterSince(r.issued_date ?? r.retirement_date ?? "", sinceDate),
          ),
    [allGear, sinceDate],
  );
  const uniforms = useMemo(
    () =>
      !sinceDate
        ? allUniforms
        : allUniforms.filter((r) =>
            isAfterSince(
              ((r as Record<string, unknown>)["issued_date"] as string) ?? "",
              sinceDate,
            ),
          ),
    [allUniforms, sinceDate],
  );
  // SCBA cylinders and fit tests are compliance/readiness data — they are NOT
  // date-range filtered (an expired cylinder or overdue fit test must show
  // regardless of the lookback window). They are consumed below directly from
  // the full allScba/allScbaG1/allFitG1/allFitAdv sets.
  const disposals = useMemo(
    () =>
      !sinceDate
        ? allDisposals
        : allDisposals.filter((r) =>
            isAfterSince(r.date_of_disposal ?? "", sinceDate),
          ),
    [allDisposals, sinceDate],
  );
  const collection = useMemo(
    () =>
      !sinceDate
        ? allCollection
        : allCollection.filter((r) =>
            isAfterSince(r.created_on ?? "", sinceDate),
          ),
    [allCollection, sinceDate],
  );

  const agg = aggData?.aggregates ?? {};
  // Cross-check only — the pre-computed aggregate is refreshed off-repo and can
  // go stale. It never drives the headline (see gearExpiryState below).
  const aggExpiredCount = coerceCount(agg["gear_expired_count"]);

  const isAnyLoading =
    gearLoading ||
    uniformLoading ||
    scbaLoading ||
    scbaG1Loading ||
    fitG1Loading ||
    fitAdvLoading ||
    disposalLoading ||
    collectionLoading;

  // ── KPI computations ──────────────────────────────────────────────────────

  // Readiness/compliance metrics (expired gear, SCBA end-of-life, fit-test
  // non-compliance) are computed over the FULL inventory, never the date-range
  // filtered subset. A lookback window must not be able to hide an expired or
  // overdue item — those are exactly the at-risk records a readiness dashboard
  // exists to surface. The date toggle scopes only the activity/recency views
  // (disposals, gear collection, unissued inventory) below.
  const gearAlerts = useMemo(() => computeGearAlerts(allGear), [allGear]);

  // Expiry headline: whole-table server summary first, page of rows as backup.
  const gearExpirySummary = useMemo(
    () => readGearExpirySummary(gearData),
    [gearData],
  );
  const gearExpiry = useMemo(
    () => computeGearExpiryState(allGear, gearExpirySummary),
    [allGear, gearExpirySummary],
  );
  const expiredTile = useMemo(() => gearExpiryTile(gearExpiry), [gearExpiry]);
  const expiredReconNote = useMemo(
    () => aggregateReconNote(gearExpiry, aggExpiredCount),
    [gearExpiry, aggExpiredCount],
  );

  const activeGear = useMemo(() => computeActiveGear(gear), [gear]);
  const unissuedCounts = useMemo(
    () => computeUnissuedCounts(gear, uniforms),
    [gear, uniforms],
  );
  const unissuedGearCount = unissuedCounts.unissued;
  const pendingCollectionCount = useMemo(() => collection.length, [collection]);

  // ── Section 1: Gear expiration alert list ─────────────────────────────────

  const gearAlertItems = useMemo<AlertItem[]>(
    () => gearAlerts.map((a, i) => gearAlertToAlertItem(a, i)),
    [gearAlerts],
  );

  // ── Section 2: SCBA stats ─────────────────────────────────────────────────

  const scbaStats = useMemo(
    () => computeScbaStats(allScba, allScbaG1),
    [allScba, allScbaG1],
  );
  const scbaCurrentTile = useMemo(
    () => scbaCurrentTileFor(scbaStats),
    [scbaStats],
  );

  // ── Section 3: Unissued inventory ─────────────────────────────────────────

  const unissuedGroups = useMemo(
    () => computeUnissued(gear, uniforms),
    [gear, uniforms],
  );

  // ── Section 4: Gear collection alerts ────────────────────────────────────

  const collectionAlerts = useMemo(
    () => computeCollectionAlerts(collection),
    [collection],
  );
  const collectionAlertItems = useMemo<AlertItem[]>(
    () => collectionAlerts.map(collectionToAlertItem),
    [collectionAlerts],
  );

  // ── Section 5: Fit test compliance ───────────────────────────────────────

  const fitTestStatuses = useMemo(
    () => computeFitTestStatuses(allFitG1, allFitAdv),
    [allFitG1, allFitAdv],
  );
  const fitTestDonutData = useMemo(
    () => computeFitTestDonut(fitTestStatuses),
    [fitTestStatuses],
  );
  const fitTestLoading = fitG1Loading || fitAdvLoading;

  // ── Section 6: Disposal trends ────────────────────────────────────────────

  const disposalTrends = useMemo(
    () => computeDisposalTrends(disposals),
    [disposals],
  );

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto space-y-6 md:space-y-8 overflow-x-hidden">
      {/* ── Page header ─────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-theme-primary">
            Equipment &amp; Gear Readiness
          </h1>
          <p className="text-theme-secondary mt-1 text-sm">
            PPE lifecycle, SCBA status, inventory, fit tests, and disposal
            tracking.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <StatusBadge label="OPS data" variant="blue" />
          {isAnyLoading && (
            <span className="text-xs text-theme-muted animate-pulse">
              Loading...
            </span>
          )}
        </div>
      </div>

      {/* ── STOPGAP compliance notice (2026-08-25) ─────────────────────────
          The SCBA and fit-test tables stopped syncing on 2026-08-07, and the
          empty-state paths below render "all current" when nothing is flagged,
          which is a false-green on life-safety data (OSHA 1910.134 / NFPA 1852).
          Verified against D1 2026-08-19: 163/163 fit tests and 242/352 SCBA
          cylinders were past due while this page said "current". This banner
          stays until the data-path fix ships; remove it in that change. */}
      <Banner
        variant="error"
        title="SCBA and fit-test compliance data on this page is not current"
      >
        The sync that feeds the SCBA cylinder and respirator fit-test tables
        stopped on 2026-08-07, and the department's records show fit tests and
        cylinder hydro tests past their due dates that this page does not flag.
        Do not rely on this page for compliance status. Confirm against the
        source records until this notice is removed.
      </Banner>

      {/* ── Row-completeness notice ───────────────────────────────────────
          Above the KPI row on purpose: a readiness percentage computed over a
          partial table is the same class of defect as the SCBA false-green
          above it. */}
      <TruncationNoticeGroup sources={truncationSources} />

      {/* ── Date Range Toggle ─────────────────────────────────────────────── */}
      <DateRangeToggle value={range} onChange={setRange} />

      {/* ── KPI Row ──────────────────────────────────────────────────────── */}
      <section>
        <h2 className="text-xs font-semibold text-theme-muted uppercase tracking-wider mb-4">
          Gear Readiness Overview
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            label="Expired Gear"
            value={gearLoading ? "—" : expiredTile.value}
            subtitle={
              gearLoading ? "Loading gear records…" : expiredTile.subtitle
            }
            accent={gearLoading ? "neutral" : expiredTile.accent}
            isLoading={gearLoading}
            trend={
              !gearLoading && expiredTile.trendLabel
                ? {
                    direction: expiredTile.accent === "green" ? "down" : "up",
                    label: expiredTile.trendLabel,
                    positiveIsGood: false,
                  }
                : undefined
            }
          />
          <KPICard
            label="Active Gear Items"
            value={
              gearLoading || activeGear.count === null ? "—" : activeGear.count
            }
            subtitle={
              activeGear.basis === "derived"
                ? "in service (not disposed, not expired)"
                : activeGear.basis === "none"
                  ? "no gear records in view"
                  : "items in service"
            }
            accent="neutral"
            isLoading={gearLoading}
          />
          <KPICard
            label="Unissued"
            value={
              gearLoading || uniformLoading || !unissuedCounts.resolved
                ? "—"
                : unissuedGearCount
            }
            subtitle={
              !unissuedCounts.resolved
                ? "no records in view"
                : unissuedCounts.unknown > 0
                  ? `gear + uniforms not assigned · ${unissuedCounts.unknown} unknown`
                  : "gear + uniforms not assigned"
            }
            accent="amber"
            isLoading={gearLoading || uniformLoading}
          />
          <KPICard
            label="Pending Collection"
            value={collectionLoading ? "—" : pendingCollectionCount}
            subtitle="gear recovery records"
            accent={pendingCollectionCount > 0 ? "amber" : "neutral"}
            isLoading={collectionLoading}
          />
        </div>
      </section>

      {/* ── Section 1: Gear Expiration Alerts ───────────────────────────── */}
      <section>
        <SectionHeading>Gear Expiration Alerts</SectionHeading>
        <AlertList
          title="PPE Expiration — Expired, Within 90 Days, Within 6 Months"
          items={gearAlertItems}
          emptyMessage={
            gearLoading
              ? "Loading gear records..."
              : gearExpiry.allClear
                ? "All gear is current — no expiration alerts"
                : "Gear expiry status could not be determined — no records loaded"
          }
          isLoading={gearLoading}
          maxVisible={12}
        />
        {expiredReconNote && (
          <p className="mt-2 text-xs text-status-amber">{expiredReconNote}</p>
        )}
      </section>

      {/* ── Section 2: SCBA Cylinder Status ─────────────────────────────── */}
      <section>
        <SectionHeading>SCBA Cylinder Status</SectionHeading>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <KPICard
            label="Current"
            value={scbaLoading || scbaG1Loading ? "—" : scbaCurrentTile.value}
            subtitle={scbaCurrentTile.subtitle}
            accent={scbaCurrentTile.accent}
            isLoading={scbaLoading || scbaG1Loading}
          />
          <KPICard
            label="Needs Testing"
            value={scbaLoading || scbaG1Loading ? "—" : scbaStats.needsTesting}
            subtitle="hydro / next test date has passed"
            accent={scbaStats.needsTesting > 0 ? "amber" : "neutral"}
            isLoading={scbaLoading || scbaG1Loading}
          />
          <KPICard
            label="End of Life"
            value={scbaLoading || scbaG1Loading ? "—" : scbaStats.endOfLife}
            subtitle="past recorded end-of-life date"
            accent={scbaStats.endOfLife > 0 ? "red" : "neutral"}
            isLoading={scbaLoading || scbaG1Loading}
          />
          <KPICard
            label="Unknown"
            value={scbaLoading || scbaG1Loading ? "—" : scbaStats.unknown}
            subtitle="no usable test date — NOT counted as current"
            accent={scbaStats.unknown > 0 ? "amber" : "neutral"}
            isLoading={scbaLoading || scbaG1Loading}
          />
        </div>
        {!scbaLoading && !scbaG1Loading && scbaStats.unknown > 0 && (
          <p className="mb-4 text-xs text-status-amber">
            {scbaStats.unknown} of {scbaStats.total} cylinders have no usable
            test date on file. They are excluded from the Current count — their
            compliance is unknown, not confirmed.
          </p>
        )}
        <ChartCard
          title="Cylinders Needing Attention"
          subtitle="Hydro overdue, past end-of-life, or missing a test date"
          headerRight={
            scbaStats.attentionRows.length > 0 ? (
              <StatusBadge
                label={`${scbaStats.attentionRows.length} of ${scbaStats.total} cylinders`}
                variant={scbaStats.endOfLife > 0 ? "red" : "yellow"}
              />
            ) : null
          }
          minHeight={0}
        >
          <ScbaAttentionTable
            rows={scbaStats.attentionRows}
            isLoading={scbaLoading || scbaG1Loading}
          />
        </ChartCard>
      </section>

      {/* ── Section 3: Unissued Inventory ───────────────────────────────── */}
      <section>
        <SectionHeading>Unissued Inventory</SectionHeading>
        <ChartCard
          title="Unassigned Gear &amp; Uniforms by Type"
          subtitle="Items where member_issued_to or issued_to is blank"
          headerRight={
            unissuedGroups.length > 0 ? (
              <StatusBadge
                label={`${unissuedGearCount} items`}
                variant="muted"
              />
            ) : null
          }
          minHeight={0}
        >
          <UnissuedTable
            groups={unissuedGroups}
            isLoading={gearLoading || uniformLoading}
            emptyMessage={unissuedEmptyMessage(unissuedCounts)}
          />
        </ChartCard>
      </section>

      {/* ── Section 4: Gear Assigned to Inactive Members ────────────────── */}
      <section>
        <SectionHeading>Gear Recovery</SectionHeading>
        <AlertList
          title="Gear Assigned to Departing / Inactive Members"
          items={collectionAlertItems}
          emptyMessage={
            collectionLoading
              ? "Loading collection records..."
              : "No outstanding gear recovery records"
          }
          isLoading={collectionLoading}
          maxVisible={10}
        />
      </section>

      {/* ── Section 5: Fit Test Compliance ──────────────────────────────── */}
      <section>
        <SectionHeading>Fit Test Compliance</SectionHeading>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard
            title="Fit Test Status — G1 + Advantage"
            subtitle="Current / Expiring (<90 days) / Expired / No Record"
            minHeight={220}
          >
            <FitTestDonut data={fitTestDonutData} isLoading={fitTestLoading} />
          </ChartCard>
          <ChartCard
            title="Non-Compliant Members"
            subtitle="Expired or expiring fit tests requiring immediate action"
            headerRight={
              fitTestStatuses.filter(
                (s) => s.status !== "current" && s.status !== "no_record",
              ).length > 0 ? (
                <StatusBadge
                  label={`${fitTestStatuses.filter((s) => s.status === "expired").length} expired`}
                  variant="red"
                />
              ) : null
            }
            minHeight={0}
          >
            <FitTestNonCompliantTable
              statuses={fitTestStatuses}
              isLoading={fitTestLoading}
            />
          </ChartCard>
        </div>
      </section>

      {/* ── Section 6: Disposal Trends ───────────────────────────────────── */}
      <section>
        <SectionHeading>Disposal Trends</SectionHeading>
        <ChartCard
          title="Equipment Disposals by Month"
          subtitle="Last 18 months — count of disposed items per month"
          headerRight={
            disposals.length > 0 ? (
              <StatusBadge
                label={`${disposals.length} total records`}
                variant="muted"
              />
            ) : null
          }
          minHeight={260}
        >
          <DisposalBarChart data={disposalTrends} isLoading={disposalLoading} />
        </ChartCard>
      </section>
    </div>
  );
}
